import {
  AssignDesignCropsRequest,
  AssignDesignCropsResponse,
  BoxAction,
  CameraDetails,
  CameraImageResponse,
  CameraImageTimelineResponse,
  CandidatesSearchParams,
  CoffeeDetectionParams,
  ControlModEligibilityBatchRequest,
  ControlModEligibilityBatchResponse,
  ControlModProcessingTypeBatchRequest,
  ControlPogResetMappingRun,
  ControlPogResetModsResponse,
  ControlPogResetStatsResponse,
  ControlPogResetTaskDetailResponse,
  ControlPogResetTasksResponse,
  CreateDesignResponse,
  CreateEmbeddingsCropMapping,
  CreateEmptyDesignRequest,
  CropEmbeddingSearchResponse,
  DesignSearchParams,
  DesignSearchResponse,
  DeviceWakeUpParams,
  EmbeddingCandidateResponse,
  EmbeddingCountByBarcode,
  EmbeddingSearchParams,
  FlexSpaceDetectionParams,
  OosDetectionParams,
  OosDetectionResponse,
  OosDetectionState,
  PlanogramComplianceBox,
  PlanogramComplianceConfigItem,
  PlanogramCompliancePayload,
  PlanogramComplianceResponse,
  PogCompTemplateBox,
  PogResetTaskStatusFilter,
  ProduceVisionParams,
  ProductBarcodeResponse,
  ProductImage,
  ProductImageCreateResponse,
  ProductImageReorderItem,
  ProductRecognitionCropScanUpdate,
  ProductRecognitionImageCropScansResponse,
  ProductRecognitionResponse,
  ProductVariantResponse,
  ReportedIssueResponse,
  ReportIssuePayload,
  StatusResponse,
  Store,
  StoreProductSearchResponse,
  Task,
  TaskLockStatusResponse,
  TaskPogResponse,
  TasksSearchParams,
  UpcCorrectionPayload,
  UpdatedEmbeddingsResponse,
} from '../types';
import { getOosDetections, getProduceActions, getProduceVision } from '../utils';
const API_MAIN_URL = process.env.REACT_APP_API_MAIN_URL!;
const API_LEGACY = process.env.REACT_APP_API_LEGACY_URL!;
const API_DL_URL = process.env.REACT_APP_API_DL_URL!;

// CDN URLs - handle both single URL (staging) and multiple URLs (production)
const getCdnUrls = (): string[] => {
  if (process.env.REACT_APP_CDN_URLS) {
    const cdnUrls = JSON.parse(process.env.REACT_APP_CDN_URLS);
    return Object.values(cdnUrls);
  }
  if (process.env.REACT_APP_CDN_URL) {
    return [process.env.REACT_APP_CDN_URL];
  }
  throw new Error('No CDN URLs configured');
};

const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

const CDN_URLS = getCdnUrls();

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

let noAuthCallback: (() => void) | null = null;

const STORES_CACHE_DURATION = 60 * 60 * 1000; // 60 minutes
const STORES_CACHE_KEY = 'focal_stores_cache';

const CAMERAS_CACHE_DURATION = 60 * 60 * 1000; // 60 minutes
const CAMERAS_CACHE_KEY_PREFIX = 'focal_cameras_cache:';

function camerasCacheKey(storeName: string): string {
  return `${CAMERAS_CACHE_KEY_PREFIX}${storeName}`;
}

export function setNoAuthCallback(callback: () => void): void {
  noAuthCallback = callback;
}

export function clearStoresCache(): void {
  localStorage.removeItem(STORES_CACHE_KEY);
}

export function clearCamerasCache(storeName?: string): void {
  if (storeName) {
    localStorage.removeItem(camerasCacheKey(storeName));
    return;
  }

  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(CAMERAS_CACHE_KEY_PREFIX)) {
      localStorage.removeItem(key);
    }
  }
}

function getCamerasFromCache(token: string, storeName: string): CameraDetails[] | null {
  const key = camerasCacheKey(storeName);
  try {
    const cached = localStorage.getItem(key);
    if (!cached) {
      return null;
    }

    const { data, timestamp, token: cachedToken } = JSON.parse(cached);
    const now = Date.now();

    if (cachedToken === token && now - timestamp < CAMERAS_CACHE_DURATION) {
      return data;
    }

    localStorage.removeItem(key);
    return null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function setCamerasToCache(cameras: CameraDetails[], token: string, storeName: string): void {
  try {
    const cacheData = {
      data: cameras,
      timestamp: Date.now(),
      token,
    };
    localStorage.setItem(camerasCacheKey(storeName), JSON.stringify(cacheData));
  } catch {
    // localStorage might be full or unavailable, fail silently
  }
}

function getStoresFromCache(token: string): Store[] | null {
  try {
    const cached = localStorage.getItem(STORES_CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp, token: cachedToken } = JSON.parse(cached);
    const now = Date.now();

    if (cachedToken === token && now - timestamp < STORES_CACHE_DURATION) {
      return data;
    }

    // Cache expired or token mismatch, remove it
    localStorage.removeItem(STORES_CACHE_KEY);
    return null;
  } catch {
    // Invalid cache data, remove it
    localStorage.removeItem(STORES_CACHE_KEY);
    return null;
  }
}

function setStoresToCache(stores: Store[], token: string): void {
  try {
    const cacheData = {
      data: stores,
      timestamp: Date.now(),
      token: token,
    };
    localStorage.setItem(STORES_CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // localStorage might be full or unavailable, fail silently
  }
}

const normalizePlanogramComplianceBoxes = (boxes: PlanogramComplianceBox[]): any[] => {
  return (
    boxes
      // .filter(item => !item.ignore)
      .map((item) => {
        // Replace il_item_status "low" with "in"
        const normalizedIlItemStatus =
          item.osaStatus === OosDetectionState.LOW ? OosDetectionState.IN : item.osaStatus;

        // If box_status is null or "na", use normalizedIlItemStatus
        const normalizedBoxStatus =
          item.complianceStatus === null ? normalizedIlItemStatus : item.complianceStatus;

        const normalizedBoxUpc = item.complianceStatus === null ? item.pogItemUpc : item.rogItemUpc;

        // Calculate shape (avoid TS error with nulls)
        const { x1, y1, x2, y2 } = item;
        const hasCoords = x1 !== null && x2 !== null && y1 !== null && y2 !== null;
        const shape: [number, number, number, number] = hasCoords
          ? [Math.min(x1!, x2!), Math.min(y1!, y2!), Math.abs(x2! - x1!), Math.abs(y2! - y1!)]
          : [0, 0, 0, 0];

        return {
          ...item,
          ilItemStatus: normalizedIlItemStatus,
          boxStatus: normalizedBoxStatus,
          boxUpc: normalizedBoxUpc,
          shape,
        };
      })
  );
};

export const composeProductRecognitionFieldsWithShapes = (
  data: ProductRecognitionResponse
): Partial<ProductRecognitionResponse> => {
  // Calculate shapes for all recognition items
  const calculateShapeForBBox = (item: any): [number, number, number, number] => {
    const x = Math.min(item.x1, item.x2);
    const y = Math.min(item.y1, item.y2);
    const width = Math.abs(item.x2 - item.x1);
    const height = Math.abs(item.y2 - item.y1);
    return [x, y, width, height];
  };

  const calculateShapeForPolygon = (item: any): [number, number, number, number] => {
    if (!item.points || item.points.length === 0) return [0, 0, 0, 0];

    const xCoords = item.points.map((p: any) => p.x);
    const yCoords = item.points.map((p: any) => p.y);
    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    return [minX, minY, maxX - minX, maxY - minY];
  };

  const overwrites: Partial<ProductRecognitionResponse> = {};

  if (data.productPolygons && !data.productBoxes) {
    overwrites.isV2 = true;
    overwrites.productBoxes = data.productPolygons.map(({ points, ...rest }) => {
      const xCoords = points?.map((p: any) => p.x) ?? [];
      const yCoords = points?.map((p: any) => p.y) ?? [];
      const x1 = Math.min(...xCoords);
      const y1 = Math.min(...yCoords);
      const x2 = Math.max(...xCoords);
      const y2 = Math.max(...yCoords);
      return {
        ...rest,
        x1,
        y1,
        x2,
        y2,
        shape: [x1, y1, x2 - x1, y2 - y1] as [number, number, number, number],
      };
    });
  }

  // Add shapes to all box arrays
  if (data.productBoxes) {
    overwrites.productBoxes = data.productBoxes.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  if (data.cartonPolygons && !data.cartonBoxes) {
    overwrites.isV2 = true;
    overwrites.cartonBoxes = data.cartonPolygons.map(({ points, ...rest }) => {
      const xCoords = points?.map((p: any) => p.x) ?? [];
      const yCoords = points?.map((p: any) => p.y) ?? [];
      const x1 = Math.min(...xCoords);
      const y1 = Math.min(...yCoords);
      const x2 = Math.max(...xCoords);
      const y2 = Math.max(...yCoords);
      return {
        ...rest,
        x1,
        y1,
        x2,
        y2,
        shape: [x1, y1, x2 - x1, y2 - y1] as [number, number, number, number],
      };
    });
  }

  if (data.cartonBoxes) {
    overwrites.cartonBoxes = data.cartonBoxes.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  if (data.priceTagBoxes) {
    overwrites.priceTagBoxes = data.priceTagBoxes.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  if (data.modPolygons) {
    overwrites.modPolygons = data.modPolygons.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  if (data.shelfBoxes) {
    overwrites.shelfBoxes = data.shelfBoxes.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  if (data.templateBoxes) {
    overwrites.templateBoxes = data.templateBoxes.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  if (data.templateBoxesUp) {
    overwrites.templateBoxesUp = data.templateBoxesUp.map((item) => ({
      ...item,
      shape: calculateShapeForBBox(item),
    }));
  }

  // Add shapes to all polygon arrays
  if (data.productPolygons) {
    overwrites.productPolygons = data.productPolygons.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.visualObstructions) {
    overwrites.visualObstructions = data.visualObstructions.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.merchandise) {
    overwrites.merchandise = data.merchandise.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.cartonPolygons) {
    overwrites.cartonPolygons = data.cartonPolygons.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.hookArea) {
    overwrites.hookArea = data.hookArea.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.fridgeDoors) {
    overwrites.fridgeDoors = data.fridgeDoors.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.topStock) {
    overwrites.topStock = data.topStock.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  if (data.shelfEdges) {
    overwrites.shelfEdges = data.shelfEdges.map((item) => ({
      ...item,
      shape: calculateShapeForPolygon(item),
    }));
  }

  // Deduplicate vizSimCandidates by itemNumber within each detection,
  // collecting all normalizedBarcodes from duplicates onto the first occurrence
  Object.keys(overwrites).forEach((key) => {
    const layer = overwrites[key as keyof ProductRecognitionResponse];
    if (Array.isArray(layer)) {
      layer.forEach((detection: any) => {
        if (detection.vizSimCandidates && Array.isArray(detection.vizSimCandidates)) {
          const grouped = new Map<string, any[]>();
          const result: any[] = [];

          for (const candidate of detection.vizSimCandidates) {
            if (!candidate.itemNumber) {
              result.push(candidate);
              continue;
            }
            if (!grouped.has(candidate.itemNumber)) {
              grouped.set(candidate.itemNumber, []);
              result.push(candidate);
            }
            grouped.get(candidate.itemNumber)!.push(candidate);
          }

          for (const candidate of result) {
            if (candidate.itemNumber && grouped.has(candidate.itemNumber)) {
              const duplicates = grouped.get(candidate.itemNumber)!;
              const barcodes = [...new Set(duplicates.map((d: any) => d.normalizedBarcode))];
              candidate.allNormalizedBarcodes = barcodes;
            }
          }

          detection.vizSimCandidates = result;
        }
      });
    }
  });

  return overwrites;
};

const applyBBoxFallbackImage = <
  T extends {
    imageUri: string | null;
    productImageUri?: string | null;
    hasFallbackImage?: boolean;
  },
>(
  bboxes: T[]
): T[] => {
  return bboxes.map((bbox) => {
    if (!bbox.imageUri && bbox.productImageUri) {
      return { ...bbox, imageUri: bbox.productImageUri, hasFallbackImage: true };
    }
    return { ...bbox, hasFallbackImage: false };
  });
};

export class ApiService {
  static handleError(response: Response) {
    if (response.status === 401) {
      clearStoresCache();
      clearCamerasCache();

      if (noAuthCallback) {
        noAuthCallback();
      }
    }
  }

  static async login(username: string, password: string): Promise<string> {
    const response = await fetch(`${API_LEGACY}/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        source: 'action',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Invalid username or password');
    }

    const data = await response.json();
    return data.token;
  }

  static async extendToken(token: string): Promise<string> {
    const response = await fetch(`${API_LEGACY}/v2/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Error extending token', response.status);
    }

    const data = await response.json();
    return data.token;
  }

  static async fetchStores(token: string): Promise<Store[]> {
    // Check cache first
    const cachedStores = getStoresFromCache(token);
    if (cachedStores) {
      return cachedStores;
    }

    const response = await fetch(`${API_DL_URL}/v1/control/stores`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading stores');
    }

    const stores = await response.json();
    const sortedStores = stores.sort((a: Store, b: Store) =>
      a.legibleName.localeCompare(b.legibleName, 'en', { numeric: true })
    );

    // Cache the results
    setStoresToCache(sortedStores, token);

    return sortedStores;
  }

  private static async _fetchCameras(token: string, storeName: string): Promise<CameraDetails[]> {
    const response = await fetch(`${API_DL_URL}/v1/control/${storeName}/cameras`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading cameras list');
    }

    return response.json();
  }

  static async fetchCameras(
    token: string,
    storeName: string,
    { useCache } = { useCache: true }
  ): Promise<CameraDetails[]> {
    if (useCache) {
      const cachedCameras = getCamerasFromCache(token, storeName);
      if (cachedCameras) {
        return cachedCameras;
      }
    }

    const cameras = await this._fetchCameras(token, storeName);
    setCamerasToCache(cameras, token, storeName);
    return cameras;
  }

  static async enableModEligibility(
    token: string,
    storeName: string,
    payload: ControlModEligibilityBatchRequest
  ): Promise<ControlModEligibilityBatchResponse> {
    const response = await fetch(`${API_DL_URL}/v1/control/${storeName}/mod-eligibility/enable`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error enabling mod eligibility');
    }

    return response.json();
  }

  static async disableModEligibility(
    token: string,
    storeName: string,
    payload: ControlModEligibilityBatchRequest
  ): Promise<ControlModEligibilityBatchResponse> {
    const response = await fetch(`${API_DL_URL}/v1/control/${storeName}/mod-eligibility/disable`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error disabling mod eligibility');
    }

    return response.json();
  }

  static async setModProcessingType(
    token: string,
    storeName: string,
    payload: ControlModProcessingTypeBatchRequest
  ): Promise<ControlModEligibilityBatchResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/mod-eligibility/processing-type`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error setting processing type');
    }

    return response.json();
  }

  static async fetchPogResetTasks(
    token: string,
    storeName: string,
    status: PogResetTaskStatusFilter = 'open'
  ): Promise<ControlPogResetTasksResponse> {
    const queryParams = new URLSearchParams({ status });
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/pog-reset/tasks?${queryParams.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading POG reset tasks');
    }

    return response.json();
  }

  static async fetchPogResetMods(
    token: string,
    storeName: string
  ): Promise<ControlPogResetModsResponse> {
    const response = await fetch(`${API_DL_URL}/v1/control/${storeName}/pog-reset/mods`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading POG reset mod suggestions');
    }

    return response.json();
  }

  static async startPogResetMappingRun(
    token: string,
    storeName: string,
    payload: { force: boolean; tagPrefix?: string; skipPr?: boolean }
  ): Promise<ControlPogResetMappingRun> {
    const response = await fetch(`${API_DL_URL}/v1/control/${storeName}/pog-reset/mappings`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error starting POG reset mapping generation');
    }

    return response.json();
  }

  static async fetchPogResetStats(
    token: string,
    storeName: string
  ): Promise<ControlPogResetStatsResponse> {
    const response = await fetch(`${API_DL_URL}/v1/control/${storeName}/pog-reset/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading POG reset statistics');
    }

    return response.json();
  }

  static async fetchPogResetMappingRun(
    token: string,
    storeName: string,
    runId: string
  ): Promise<ControlPogResetMappingRun> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/pog-reset/mappings/runs/${runId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading POG reset mapping run');
    }

    return response.json();
  }

  static async setPogResetTaskReviewed(
    token: string,
    storeName: string,
    taskId: number,
    reviewed: boolean
  ): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/pog-reset/tasks/${taskId}/reviewed`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviewed }),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating reviewed status');
    }
  }

  static async savePogResetConfirmedMapping(
    token: string,
    storeName: string,
    taskId: number,
    payload: {
      isMirrored: boolean;
      mods: { modNumber: number; tagName: string | null; source: string }[];
    }
  ): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/pog-reset/tasks/${taskId}/confirmed-mapping`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error saving confirmed mapping');
    }
  }

  static async fetchPogResetTaskDetail(
    token: string,
    storeName: string,
    taskId: number,
    options?: { matchThreshold?: number; similarDelta?: number; countActiveEmbeddings?: boolean }
  ): Promise<ControlPogResetTaskDetailResponse | null> {
    const queryParams = new URLSearchParams();
    if (options?.matchThreshold !== undefined) {
      queryParams.set('match_threshold', String(options.matchThreshold));
    }
    if (options?.similarDelta !== undefined) {
      queryParams.set('similar_delta', String(options.similarDelta));
    }
    if (options?.countActiveEmbeddings) {
      queryParams.set('count_active_embeddings', 'true');
    }
    const query = queryParams.toString();
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/pog-reset/tasks/${taskId}${query ? `?${query}` : ''}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    ApiService.handleError(response);

    // 404 means the task is unknown (no cached mappings and no live task doc).
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Error loading POG reset task details');
    }

    return response.json();
  }

  static async fetchCameraTimeline(
    token: string,
    storeName: string,
    deviceName: string
  ): Promise<CameraImageTimelineResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/cameras/${deviceName}/image-timeline`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading camera timeline');
    }

    const data: CameraImageTimelineResponse = await response.json();

    for (const template of Object.values(data.templates)) {
      template.bboxes = applyBBoxFallbackImage(template.bboxes);
    }
    for (const pog of Object.values(data.pogs)) {
      pog.bboxes = applyBBoxFallbackImage(pog.bboxes);
    }

    return data;
  }

  static async runRayProductRecognition(
    token: string,
    imageId: number,
    useCachedResults = false,
    triggerPogCompliance = false
  ): Promise<ProductRecognitionResponse> {
    const queryParams = new URLSearchParams({
      fetchProducts: 'true',
    });

    if (useCachedResults) {
      queryParams.append('useCachedResults', 'true');
    } else if (triggerPogCompliance) {
      queryParams.append('triggerPogCompliance', 'true');
    }

    const response = await fetch(
      `${API_DL_URL}/v1/control/product-recognition/${imageId}?${queryParams.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading product recognition results');
    }

    const data: ProductRecognitionResponse = await response.json();

    const fieldsWithShapes = composeProductRecognitionFieldsWithShapes(data);
    return { ...data, ...fieldsWithShapes };
  }

  static async runRayPlanogramCompliance(
    token: string,
    imageId: number,
    isV2: boolean = true,
    config: Record<string, unknown>
  ): Promise<PlanogramComplianceResponse> {
    // Only isV2 stays as query param
    const queryParams = new URLSearchParams();
    queryParams.append('v2', String(isV2));

    const endpoint = `${API_DL_URL}/v1/control/planogram-compliance/${imageId}?${queryParams.toString()}`;
    const requestBody: Record<string, any> = {};

    // Send all config values in request body
    Object.entries(config).forEach(([key, value]) => {
      requestBody[toSnakeCase(key)] = value;
    });

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading planogram compliance results');
    }

    const data: PlanogramComplianceResponse = await response.json();

    data.boxes = normalizePlanogramComplianceBoxes(data.boxes);

    // add imageId to response if not present
    if (data.imageId === undefined) {
      data.imageId = imageId;
    }

    return data;
  }

  static async fetchPlanogramComplianceConfigs(
    token: string,
    imageId: number
  ): Promise<PlanogramComplianceConfigItem[]> {
    const endpoint = `${API_DL_URL}/v1/control/planogram-compliance/configs/${imageId}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching planogram compliance configs');
    }

    return response.json();
  }

  static async fetchPogCompTemplateBoxes(
    token: string,
    imageId: number
  ): Promise<PogCompTemplateBox[]> {
    const endpoint = `${API_DL_URL}/v1/control/planogram-compliance/template-boxes/${imageId}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching pog comp template boxes');
    }

    return response.json();
  }

  static async fetchTaskPlanogramCompliance(
    token: string,
    taskId: number
  ): Promise<PlanogramComplianceResponse> {
    let endpoint = `${API_DL_URL}/v1/control/planogram-compliance/task/${taskId}`;

    const response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading planogram compliance results');
    }

    const data: PlanogramComplianceResponse = await response.json();

    data.boxes = normalizePlanogramComplianceBoxes(data.boxes);

    return data;
  }

  static async fetchTaskPog(token: string, taskId: number): Promise<TaskPogResponse> {
    const endpoint = `${API_DL_URL}/v1/control/planogram-compliance/task/${taskId}/pog`;
    const response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error loading task POG');
    }

    const data: TaskPogResponse = await response.json();

    for (const pog of Object.values(data.pogs)) {
      pog.bboxes = applyBBoxFallbackImage(pog.bboxes);
    }

    return data;
  }

  static async createPlanogramComplianceFromPayload(
    token: string,
    payload: PlanogramCompliancePayload,
    isV2?: boolean
  ): Promise<any> {
    // Remove artificially added shape
    const cleanedPayload: PlanogramCompliancePayload = JSON.parse(JSON.stringify(payload));

    // Remove cartonBoxes and productBoxes - they are artificially created only for UI
    delete (cleanedPayload as any).cartonBoxes;
    delete (cleanedPayload as any).productBoxes;

    Object.keys(cleanedPayload).forEach((layerKey) => {
      const layer = cleanedPayload[layerKey as keyof ProductRecognitionResponse];
      if (Array.isArray(layer)) {
        layer.forEach((detection: any) => {
          if (detection.shape) {
            delete detection.shape;
          }
        });
      }
    });

    // Only isV2 stays as query param
    const queryParams = new URLSearchParams();
    if (isV2 !== undefined) {
      queryParams.append('v2', String(isV2));
    }

    const requestBody: Record<string, any> = {
      ...cleanedPayload,
    };

    if (requestBody.config) {
      // Convert config keys to snake_case and empty arrays to null
      const snakeCaseConfig: Record<string, any> = {};

      Object.entries(requestBody.config).forEach(([key, value]) => {
        const snakeKey = toSnakeCase(key);
        snakeCaseConfig[snakeKey] = Array.isArray(value) && value.length === 0 ? null : value;
      });

      requestBody.config = snakeCaseConfig;
    }

    const response = await fetch(
      `${API_DL_URL}/v1/control/planogram-compliance/create_from_payload?${queryParams.toString()}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error creating planogram compliance from payload');
    }

    const data: PlanogramComplianceResponse = await response.json();

    data.boxes = normalizePlanogramComplianceBoxes(data.boxes);

    return data;
  }

  static async createProductRecognitionFromPayload(token: string, payload: any): Promise<any> {
    // Remove cartonBoxes and productBoxes - they are artificially created only for UI
    const cleanedPayload = { ...payload };
    delete cleanedPayload.cartonBoxes;
    delete cleanedPayload.productBoxes;

    const response = await fetch(
      `${API_DL_URL}/v1/control/product-recognition/create_from_payload`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedPayload),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error creating product recognition from payload');
    }

    return response.json();
  }

  static async loginCdn(token: string): Promise<void> {
    const authPromises = CDN_URLS.map((cdnUrl) =>
      fetch(`${cdnUrl}/auth/`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
        credentials: 'include',
      })
    );

    await Promise.all(authPromises);
  }

  static async sendActions(
    token: string,
    storeName: string,
    deviceName: string,
    initialImageId: number | null = null,
    actions: BoxAction[]
  ): Promise<void> {
    let lastImageId: number | null = null;

    const oosDetections = getOosDetections(actions);

    for (const oosDetection of oosDetections) {
      if (!oosDetection.params.detections.length) {
        continue; // Skip if no detections
      }
      const response = await ApiService.oosDetection(
        token,
        storeName,
        oosDetection.tagName,
        oosDetection.params
      );
      lastImageId = response.image_id;
    }

    const produceActions = getProduceActions(actions);

    if (produceActions.length === 0) {
      return; // Nothing to do
    }

    if (!lastImageId) {
      const { imageId } = await ApiService.deviceWakeUp(token, deviceName, {
        image_id: initialImageId,
      });
      lastImageId = imageId;
    }

    if (!lastImageId) {
      throw new Error('No image ID returned from device wake up');
    }

    const produceVision = getProduceVision(produceActions, lastImageId);

    // const flexSpaceDetections = getFlexSpaceDetections(actions, lastImageId);
    // const coffeeDetections = getCoffeeDetections(actions, lastImageId);
    // for (const flexSpaceDetection of flexSpaceDetections) {
    //   await ApiService.flexSpaceDetection(
    //     token,
    //     storeName,
    //     flexSpaceDetection.tagName,
    //     flexSpaceDetection.params
    //   );
    // }

    // if (coffeeDetections) {
    //   await ApiService.coffeeDetection(
    //     token,
    //     storeName,
    //     coffeeDetections.params,
    //     true // ignore suppression for coffee detection
    //   );
    // }
    if (produceVision) {
      await ApiService.produceVision(
        token,
        storeName,
        produceVision.params,
        true // ignore suppression for produce vision
      );
    }
  }

  static async runCamera(token: string, deviceName: string, imageId?: number): Promise<void> {
    await ApiService.deviceWakeUp(token, deviceName, {
      image_id: imageId || null,
    });
  }

  static async deviceWakeUp(
    token: string,
    deviceName: string,
    params: Partial<DeviceWakeUpParams> = {}
  ): Promise<{
    storeId: number;
    deviceId: number;
    deviceName: string;
    cameraId: number;
    statusId: number | null;
    imageId: number | null;
    wakeUpResponses: Record<string, any>;
  }> {
    const response = await fetch(`${API_MAIN_URL}/v1/qa/devices/${deviceName}/status`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_id: null,
        boot_id: null,
        ota_version: null,
        config: null,
        schedule_id: null,
        status_payload: null,
        image_payload: null,
        skip_image: null,
        alarm_wake_up: null,
        ...params,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error waking up device');
    }
    return await response.json();
  }

  static async reportIssue(
    token: string,
    payload: ReportIssuePayload
  ): Promise<ReportedIssueResponse> {
    const response = await fetch(`${API_DL_URL}/v1/image/report-issue`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error reporting issue');
    }
    return await response.json();
  }

  static async oosDetection(
    token: string,
    storeName: string,
    tagName: string,
    params: OosDetectionParams
  ): Promise<OosDetectionResponse> {
    const response = await fetch(
      `${API_MAIN_URL}/v1/qa/${storeName}/action-tasks/oos_detection/${tagName}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error oos detection');
    }
    return await response.json();
  }

  static async flexSpaceDetection(
    token: string,
    storeName: string,
    tagName: string,
    params: FlexSpaceDetectionParams
  ): Promise<void> {
    const response = await fetch(`${API_MAIN_URL}/v1/qa/${storeName}/flex-space-out/${tagName}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error flex space detection');
    }
    await response.json();
  }

  static async coffeeDetection(
    token: string,
    storeName: string,
    params: CoffeeDetectionParams,
    ignoreSuppression: boolean = false
  ): Promise<void> {
    const response = await fetch(
      `${API_MAIN_URL}/v1/qa/${storeName}/costa-coffee-message?ignore_suppression=${ignoreSuppression}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error coffee detection');
    }
    await response.json();
  }

  static async produceVision(
    token: string,
    storeName: string,
    params: ProduceVisionParams,
    ignoreSuppression: boolean = false
  ): Promise<void> {
    const response = await fetch(
      `${API_MAIN_URL}/v1/qa/${storeName}/produce-vision-message?ignore_suppression=${ignoreSuppression}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error producing vision');
    }
    await response.json();
  }

  static async searchEmbeddings(
    token: string,
    params: EmbeddingSearchParams
  ): Promise<CropEmbeddingSearchResponse[]> {
    const searchParams = new URLSearchParams();

    // Helper function to convert camelCase to snake_case
    const toSnakeCase = (str: string): string => {
      return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    // Add required parameter
    searchParams.append('chain_id', params.chainId.toString());

    // Add optional parameters
    if (params.storeId !== undefined) {
      searchParams.append('store_id', params.storeId.toString());
    }
    if (params.normalizedBarcodes !== undefined) {
      searchParams.append('normalized_barcodes', params.normalizedBarcodes);
    }
    if (params.isActive !== undefined) {
      searchParams.append('is_active', params.isActive.toString());
    }
    if (params.source !== undefined) {
      searchParams.append('source', params.source);
    }
    if (params.extendedSearch !== undefined) {
      searchParams.append('extended_search', params.extendedSearch.toString());
    }
    if (params.includeItemNumber !== undefined) {
      searchParams.append('include_item_number', params.includeItemNumber.toString());
    }
    if (params.createdBy !== undefined) {
      searchParams.append('created_by', params.createdBy);
    }
    if (params.sortField !== undefined) {
      // Ensure sort_field is always in snake_case
      const snakeCaseSortField = toSnakeCase(params.sortField);
      searchParams.append('sort_field', snakeCaseSortField);
    }
    if (params.sortOrder !== undefined) {
      searchParams.append('sort_order', params.sortOrder);
    }
    if (params.page !== undefined) {
      searchParams.append('page', params.page.toString());
    }
    if (params.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }

    if (params.barcode) {
      searchParams.append('barcodes', params.barcode);
    }

    const basePath =
      params.itemNumber && !params.barcode
        ? `${API_DL_URL}/v1/crops/${encodeURIComponent(params.itemNumber)}`
        : `${API_DL_URL}/v1/control/embedding/search`;

    if (params.barcode) {
      searchParams.delete('normalized_barcodes');
      searchParams.delete('item_number');
      searchParams.delete('extended_search');
    } else if (params.itemNumber) {
      searchParams.delete('normalized_barcodes');
      searchParams.delete('extended_search');
    }

    const response = await fetch(`${basePath}?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching embedding crops');
    }

    return response.json();
  }

  static async searchEmbeddingsByCropIds(
    token: string,
    embeddingCropIds: string[]
  ): Promise<CropEmbeddingSearchResponse[]> {
    const searchParams = new URLSearchParams();
    embeddingCropIds.forEach((id) => {
      searchParams.append('embedding_crop_ids', id);
    });
    searchParams.append('include_item_number', 'false');

    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/by-ids?${searchParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching embedding crops by ids');
    }

    return response.json();
  }

  static async getEmbeddingCountsByBarcode(
    token: string,
    chainId: number,
    barcodes: string[]
  ): Promise<EmbeddingCountByBarcode[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('chain_id', chainId.toString());
    barcodes.forEach((barcode) => {
      searchParams.append('barcodes', barcode);
    });

    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/counts-by-barcode?${searchParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching embedding counts by barcode');
    }

    return response.json();
  }

  static async updateEmbeddingActiveState(
    token: string,
    embeddingCropId: string,
    newActiveState: boolean
  ): Promise<UpdatedEmbeddingsResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/${embeddingCropId}/active?new_active_state=${
        newActiveState ? '1' : '0'
      }`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating embedding active state');
    }

    return response.json();
  }

  static async updateEmbeddingsActiveState(
    token: string,
    embeddingCropIds: string[],
    newActiveState: boolean
  ): Promise<UpdatedEmbeddingsResponse> {
    const params = new URLSearchParams();
    embeddingCropIds.forEach((id) => params.append('embedding_crop_ids', id));
    params.append('new_active_state', newActiveState ? '1' : '0');

    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/update_multiple?${params.toString()}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating embedding active state');
    }

    return response.json();
  }

  static async updateEmbeddingUpc(
    token: string,
    embeddingCropId: string,
    newUpc: string
  ): Promise<UpdatedEmbeddingsResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/${embeddingCropId}/upc?new_upc=${encodeURIComponent(
        newUpc
      )}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating embedding UPC');
    }

    return response.json();
  }

  static async updateEmbeddingClassificationType(
    token: string,
    embeddingCropId: string,
    newClassificationType: string
  ): Promise<UpdatedEmbeddingsResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/${embeddingCropId}/classification-type?new_classification_type=${encodeURIComponent(
        newClassificationType
      )}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating embedding classification type');
    }

    return response.json();
  }

  static async updateEmbeddingOrientation(
    token: string,
    embeddingCropId: string,
    newOrientation: string
  ): Promise<UpdatedEmbeddingsResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/${embeddingCropId}/orientation?new_orientation=${encodeURIComponent(
        newOrientation
      )}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating embedding orientation');
    }

    return response.json();
  }

  static async updateEmbeddingManualReview(
    token: string,
    embeddingCropId: string,
    newManualReviewState: boolean
  ): Promise<UpdatedEmbeddingsResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/${embeddingCropId}/manual-review?new_manual_review_state=${newManualReviewState}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating embedding manual review state');
    }

    return response.json();
  }

  static async createEmbeddings(
    token: string,
    imageId: string,
    payload: ProductRecognitionCropScanUpdate[]
  ): Promise<CreateEmbeddingsCropMapping[]> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/create-from-crop-scans/${imageId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error creating embeddings');
    }

    return response.json();
  }

  static async createEmbeddingFromBbox(
    token: string,
    params: {
      imageId: number;
      crops: Array<{
        normalizedBarcode?: string;
        barcode?: string;
        bbox: number[];
        productId?: number;
        classificationType?: 'product' | 'carton';
        orientation?: 'front' | 'not-front';
        productName?: string;
      }>;
      source: 'at' | 'scan' | 'control' | 'other';
      isActive: boolean;
      isPending?: boolean;
    }
  ): Promise<any> {
    const response = await fetch(
      `${API_MAIN_URL}/v1/product-recognition/embedding/create-from-bbox`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error creating embeddings from bboxes');
    }

    return response.json();
  }

  static async fetchCameraImage(
    token: string,
    imageId: string,
    { countActiveEmbeddings } = { countActiveEmbeddings: false }
  ): Promise<CameraImageResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/images/${imageId}?count_active_embeddings=${countActiveEmbeddings}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching camera image');
    }

    const data: CameraImageResponse = await response.json();

    for (const template of Object.values(data.templates)) {
      template.bboxes = applyBBoxFallbackImage(template.bboxes);
    }
    for (const pog of Object.values(data.pogs)) {
      pog.bboxes = applyBBoxFallbackImage(pog.bboxes);
    }

    return data;
  }

  static async fetchCameraLatestImage(
    token: string,
    storeName: string,
    cameraId: number,
    date: string
  ): Promise<CameraImageTimelineResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/${storeName}/cameras/${cameraId}/latest-image?date=${date}&count_active_embeddings=true`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching latest image for date');
    }

    return response.json();
  }

  static async fetchRelativeImage(
    token: string,
    imageId: string,
    rel: 'next' | 'prev'
  ): Promise<CameraImageResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/images/${imageId}?rel=${rel}&count_active_embeddings=true`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error(`Error fetching ${rel} image`);
    }

    return response.json();
  }

  static async searchCandidates(
    token: string,
    params: CandidatesSearchParams
  ): Promise<EmbeddingCandidateResponse[]> {
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      const value = params[key as keyof CandidatesSearchParams];
      if (value !== undefined) {
        searchParams.append(toSnakeCase(key), String(value));
      }
    });

    const response = await fetch(
      `${API_DL_URL}/v1/control/embedding/candidates?${searchParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching embedding crops');
    }

    return response.json();
  }

  static async searchTasks(token: string, params: TasksSearchParams): Promise<Task[]> {
    const searchParams = new URLSearchParams();

    // Handle required chainId
    searchParams.append('chain_id', String(params.chainId));

    // Handle optional params
    if (params.storeId !== undefined) {
      searchParams.append('store_id', String(params.storeId));
    }
    if (params.tagNamePrefix !== undefined) {
      searchParams.append('tag_name_prefix', params.tagNamePrefix);
    }
    if (params.reviewStatus !== undefined) {
      searchParams.append('review_status', params.reviewStatus);
    }
    if (params.taskDate !== undefined) {
      searchParams.append('task_date', params.taskDate);
    }
    if (params.isActive !== undefined) {
      searchParams.append('is_active', String(params.isActive));
    }
    if (params.isAudited !== undefined) {
      searchParams.append('audited_status', String(params.isAudited));
    }
    if (params.imageId) {
      searchParams.append('image_id', params.imageId);
    }

    // Handle paging params
    if (params.page !== undefined) {
      searchParams.append('page', String(params.page));
    }
    if (params.limit !== undefined) {
      searchParams.append('limit', String(params.limit));
    }

    // Handle sort params
    if (params.sortField !== undefined) {
      searchParams.append('sort_field', params.sortField);
    }
    if (params.sortOrder !== undefined) {
      searchParams.append('sort_order', params.sortOrder);
    }

    const response = await fetch(
      `${API_DL_URL}/v1/control/planogram-compliance/task_search?${searchParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching tasks');
    }

    return response.json();
  }

  static async fetchTaskDetail(token: string, taskId: number): Promise<ProductRecognitionResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/product-recognition/compliance_task/${taskId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching task detail');
    }

    const data = await response.json();
    const fieldsWithShapes = composeProductRecognitionFieldsWithShapes(data);
    return { ...data, ...fieldsWithShapes };
  }

  static async closeTask(
    token: string,
    storeName: string,
    taskId: string,
    action: string
  ): Promise<void> {
    const response = await fetch(`${API_LEGACY}/v1/action/${storeName}/tasks/${taskId}/resolve`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action }),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error closing task');
    }
  }

  static async startPogReset(
    token: string,
    storeName: string,
    payload: { taskId: number; tagNames: (string | null)[]; isMirrored: boolean }
  ): Promise<void> {
    const response = await fetch(`${API_MAIN_URL}/v1/action/${storeName}/pog-reset/start`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        task_id: payload.taskId,
        tag_names: payload.tagNames,
        is_mirrored: payload.isMirrored,
      }),
    });

    ApiService.handleError(response);

    if (response.status === 404) {
      throw new ApiError('One or more mods were not found in the store', 404);
    }
    if (!response.ok) {
      throw new Error('Error starting POG reset');
    }
  }

  static async resolvePogResetTask(
    token: string,
    storeName: string,
    taskId: number,
    payload: { action: string; pogResetModUpdates?: Record<string, unknown> }
  ): Promise<void> {
    const response = await fetch(`${API_LEGACY}/v1/action/${storeName}/tasks/${taskId}/resolve`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error resolving POG reset task');
    }
  }

  static async getTaskLockStatus(
    token: string,
    taskId: number,
    storeId: number
  ): Promise<TaskLockStatusResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/planogram-compliance/task_lock/${taskId}?store_id=${storeId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Fetching task status failed');
    }

    const data = await response.json();

    if (data.isLocked) {
      return {
        lockedByUserId: data.userId,
        lockedByUserName: data.userName,
        reviewAssignedAt: new Date().toISOString(),
        ttl: data.ttl,
      };
    }

    return {
      lockedByUserId: null,
      lockedByUserName: null,
      reviewAssignedAt: null,
      ttl: null,
    };
  }

  static async lockTaskForUser(token: string, taskId: number, storeId: number): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/planogram-compliance/task_lock/${taskId}?store_id=${storeId}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('Task already locked by another reviewer');
      }

      throw new Error('Locking task failed');
    }
  }

  static async unlockTask(token: string, taskId: number, storeId: number): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/planogram-compliance/task_lock/${taskId}?store_id=${storeId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Unlocking task failed');
    }
  }

  static async getProductRecognitionCropScans(
    token: string,
    imageId: number
  ): Promise<ProductRecognitionImageCropScansResponse> {
    const response = await fetch(`${API_MAIN_URL}/v1/product-recognition/crop-scans/${imageId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error(String(response.status));
    }

    return response.json();
  }

  static async createProductRecognitionCropScans(
    token: string,
    imageId: number
  ): Promise<ProductRecognitionImageCropScansResponse> {
    const response = await fetch(
      `${API_MAIN_URL}/v1/product-recognition/crop-scans/${imageId}?source=control`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error creating product recognition crop scans');
    }

    return response.json();
  }

  static async updateProductRecognitionCropScans(
    token: string,
    imageId: number,
    payload: ProductRecognitionCropScanUpdate[]
  ): Promise<StatusResponse> {
    const response = await fetch(
      `${API_MAIN_URL}/v1/product-recognition/crop-scans/${imageId}?source=control`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error updating product recognition crop scans');
    }

    return response.json();
  }

  static async finishProductRecognitionCropScanSession(
    token: string,
    imageId: number
  ): Promise<StatusResponse> {
    const response = await fetch(`${API_MAIN_URL}/v1/product-recognition/crop-scans/${imageId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error finishing product recognition crop scan session');
    }

    return response.json();
  }

  static async getStoreProductByBarcode(
    token: string,
    storeName: string,
    barcode: string,
    codeType?: 'upc' | 'item_number'
  ): Promise<ProductBarcodeResponse> {
    const query = codeType ? `?code_type=${codeType}` : '';
    const response = await fetch(
      `${API_MAIN_URL}/v1/product-recognition/stores/${storeName}/products/${barcode}${query}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching store product by barcode');
    }

    return response.json();
  }

  static async searchStoreProducts(
    token: string,
    storeName: string,
    search: string,
    limit = 30
  ): Promise<StoreProductSearchResponse> {
    const params = new URLSearchParams({ search, limit: String(limit) });
    const response = await fetch(
      `${API_LEGACY}/v1/stores/${storeName}/products/?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (response.status === 404) {
      return { products: [] };
    }

    if (!response.ok) {
      throw new Error('Error searching store products');
    }

    return response.json();
  }

  static async applyUpcCorrections(
    token: string,
    inferenceId: string,
    payload: UpcCorrectionPayload
  ): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/control/product-recognition/${inferenceId}/upc-corrections`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error applying UPC corrections');
    }

    await response.json();
  }

  // Product Image Management

  static async getProductImages(
    token: string,
    chainId: number,
    upc: string,
    storeName?: string
  ): Promise<ProductImage[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('chain_id', chainId.toString());
    searchParams.append('upc', upc);
    if (storeName) {
      searchParams.append('store_name', storeName);
      searchParams.append('include_alternate_upcs', 'true');
    }

    const response = await fetch(`${API_DL_URL}/v1/product-images?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error fetching product images');
    }

    return response.json();
  }

  static async uploadProductImage(
    token: string,
    chainId: number,
    upc: string,
    imageFile: Blob,
    position: number = 0,
    source?: string
  ): Promise<ProductImageCreateResponse> {
    const formData = new FormData();
    formData.append('file', imageFile);

    const params = new URLSearchParams({
      chain_id: chainId.toString(),
      upc,
      position: position.toString(),
    });
    if (source) params.append('source', source);

    const response = await fetch(`${API_DL_URL}/v1/control/product-images?${params}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error uploading product image');
    }

    return response.json();
  }

  static async reorderProductImages(
    token: string,
    positions: ProductImageReorderItem[]
  ): Promise<void> {
    const response = await fetch(`${API_DL_URL}/v1/control/product-images/reorder`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ positions }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error reordering product images');
    }
  }

  static async deleteProductImage(token: string, productImageId: number): Promise<void> {
    const response = await fetch(`${API_DL_URL}/v1/control/product-images/${productImageId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error deleting product image');
    }
  }

  static async searchDesigns(
    token: string,
    params: DesignSearchParams
  ): Promise<DesignSearchResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('chain_id', params.chainId.toString());
    if (params.storeName !== undefined) {
      searchParams.append('store_name', params.storeName);
    }
    params.itemNumbers?.forEach((value) => searchParams.append('item_numbers', value));
    params.normalizedBarcodes?.forEach((value) =>
      searchParams.append('normalized_barcodes', value)
    );
    if (params.barcode !== undefined) {
      searchParams.append('barcode', params.barcode);
    }
    if (params.page !== undefined) {
      searchParams.append('page', params.page.toString());
    }
    if (params.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }

    const response = await fetch(
      `${API_DL_URL}/v1/forge/designs/search?${searchParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error searching designs');
    }

    return response.json();
  }

  static async createEmptyDesign(
    token: string,
    payload: CreateEmptyDesignRequest
  ): Promise<CreateDesignResponse> {
    const response = await fetch(`${API_DL_URL}/v1/forge/designs/empty`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error creating empty design');
    }

    return response.json();
  }

  static async assignDesignCrops(
    token: string,
    payload: AssignDesignCropsRequest
  ): Promise<AssignDesignCropsResponse> {
    const response = await fetch(`${API_DL_URL}/v1/forge/crops/assign`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    ApiService.handleError(response);

    if (!response.ok) {
      throw new Error('Error assigning design crops');
    }

    return response.json();
  }

  static async fetchVariants(
    token: string,
    storeName: string,
    upc: string
  ): Promise<ProductVariantResponse[]> {
    const response = await fetch(
      `${API_DL_URL}/v1/products/${encodeURIComponent(storeName)}/variants?upc=${encodeURIComponent(upc)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw new Error('Error fetching variants');
    }
    return response.json();
  }
}
